import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton, TextField, } from "@fluentui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadPdfFromUrl, openPdfPreviewTab } from "../utils/downloadPdf";
function isValidEmail(email) {
    if (!email || email.length > 320)
        return false;
    if (/\s/.test(email))
        return false;
    const atIndex = email.indexOf("@");
    if (atIndex <= 0 || atIndex === email.length - 1)
        return false;
    const dotIndex = email.indexOf(".", atIndex + 1);
    if (dotIndex === -1 || dotIndex === email.length - 1)
        return false;
    return true;
}
export const SiteInABoxSowDialog = ({ isOpen, onDismiss, selectedItemId, onFetchDocUrl, docUrl, docUrlLoading, authoriseLoading, onAuthorise, hideAuthoriseFields, downloadFileName, LoadingComponent, PdfViewerComponent, }) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [poNumber, setPoNumber] = useState("");
    const [emailError, setEmailError] = useState("");
    const [poNumberError, setPoNumberError] = useState("");
    const [downloading, setDownloading] = useState(false);
    useEffect(() => {
        if (!isOpen) {
            setEmail("");
            setPoNumber("");
            setEmailError("");
            setPoNumberError("");
        }
    }, [isOpen]);
    // Intentionally depend only on isOpen and selectedItemId. onFetchDocUrl is
    // invoked but not listed so that consumers can pass inline arrows without
    // causing an infinite loop (new reference each render → effect → Redux update → re-render).
    useEffect(() => {
        if (!isOpen)
            return;
        if (selectedItemId === null || selectedItemId === undefined)
            return;
        onFetchDocUrl(selectedItemId);
    }, [isOpen, selectedItemId]);
    const title = useMemo(() => {
        return (_jsx("div", { style: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
            }, children: _jsx("div", { style: { display: "flex", gap: "8px", alignItems: "center" }, children: !hideAuthoriseFields && (_jsxs(_Fragment, { children: [_jsx(TextField, { placeholder: t("Enter email address"), value: email, onChange: (_, newValue) => {
                                setEmail(newValue || "");
                                setEmailError("");
                            }, type: "email", errorMessage: emailError, styles: { root: { width: "250px" } }, ariaLabel: "sow-dialog-email" }), _jsx(TextField, { placeholder: t("Enter PO number"), value: poNumber, onChange: (_, newValue) => {
                                setPoNumber(newValue || "");
                                setPoNumberError("");
                            }, errorMessage: poNumberError, styles: { root: { width: "180px" } }, ariaLabel: "sow-dialog-po-number" }), _jsx(PrimaryButton, { onClick: async () => {
                                if (!selectedItemId && selectedItemId !== 0)
                                    return;
                                const trimmedPoNumber = poNumber.trim();
                                let hasError = false;
                                if (!isValidEmail(email)) {
                                    setEmailError(t("Please enter a valid email address"));
                                    hasError = true;
                                }
                                if (!trimmedPoNumber) {
                                    setPoNumberError(t("PO number is required"));
                                    hasError = true;
                                }
                                if (hasError)
                                    return;
                                const success = await onAuthorise(selectedItemId, email, trimmedPoNumber);
                                if (success) {
                                    setEmail("");
                                    setPoNumber("");
                                    setEmailError("");
                                    setPoNumberError("");
                                    onDismiss();
                                }
                                else {
                                    setEmailError(t("Failed to authorise. Please check details and try again."));
                                }
                            }, text: t("Authorise"), disabled: !email ||
                                !poNumber.trim() ||
                                authoriseLoading ||
                                selectedItemId === null ||
                                selectedItemId === undefined, ariaLabel: "sow-dialog-authorise" })] })) }) }));
    }, [
        authoriseLoading,
        email,
        emailError,
        hideAuthoriseFields,
        onAuthorise,
        onDismiss,
        poNumber,
        poNumberError,
        selectedItemId,
        t,
    ]);
    const handleDownload = useCallback(() => {
        if (!docUrl)
            return;
        const filename = downloadFileName ??
            (selectedItemId !== null && selectedItemId !== undefined
                ? `site-in-a-box-${selectedItemId}.pdf`
                : "site-in-a-box.pdf");
        const previewTab = openPdfPreviewTab();
        setDownloading(true);
        void downloadPdfFromUrl(docUrl, filename, {
            fallbackWindow: previewTab,
        }).finally(() => {
            setDownloading(false);
        });
    }, [docUrl, downloadFileName, selectedItemId]);
    return (_jsxs(Dialog, { hidden: !isOpen, onDismiss: onDismiss, dialogContentProps: {
            type: DialogType.largeHeader,
            title,
        }, maxWidth: 1200, children: [docUrlLoading && _jsx(LoadingComponent, {}), !docUrlLoading && docUrl && _jsx(PdfViewerComponent, { docUrl: docUrl }), _jsxs(DialogFooter, { children: [_jsx(PrimaryButton, { onClick: handleDownload, text: t("Download PDF"), disabled: docUrlLoading || !docUrl || downloading, ariaLabel: "sow-dialog-download-pdf" }), _jsx(DefaultButton, { onClick: onDismiss, text: t("Close") })] })] }));
};
export default SiteInABoxSowDialog;
